import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { fetchCityCards, patchCityCard, uploadTileImage } from "@/lib/api";
import { PageHeader } from "../_shared";

export default function CityCardsTab() {
    const [cards, setCards] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const refresh = useCallback(
        async () => setCards(await fetchCityCards()),
        [],
    );
    useEffect(() => {
        refresh();
    }, [refresh]);

    return (
        <div data-testid="city-cards-tab">
            <PageHeader
                title="Orbital"
                accent="city cards."
                description="Upload an image and set the city name for each of the 10 orbiting cards."
            />
            <div
                data-testid="city-cards-grid"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-line border border-line rounded-2xl overflow-hidden"
            >
                {cards.map((c) => (
                    <CityCardSlot
                        key={c.id}
                        card={c}
                        open={editingId === c.id}
                        onToggle={() =>
                            setEditingId(editingId === c.id ? null : c.id)
                        }
                        onSaved={async () => {
                            setEditingId(null);
                            await refresh();
                            toast("City card updated.");
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

function CityCardSlot({ card, open, onToggle, onSaved }) {
    const [cityName, setCityName] = useState(card.city_name || "");
    const [imageUrl, setImageUrl] = useState(card.image_url || "");
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        setCityName(card.city_name || "");
        setImageUrl(card.image_url || "");
    }, [card, open]);

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const res = await uploadTileImage(file);
            setImageUrl(res.url);
            toast("Image uploaded.");
        } catch (err) {
            toast(err?.response?.data?.detail || "Upload failed.");
        } finally {
            setBusy(false);
        }
    };

    const onSave = async () => {
        setBusy(true);
        try {
            await patchCityCard(card.id, {
                city_name: cityName.trim(),
                image_url: imageUrl || null,
            });
            await onSaved?.();
        } catch (err) {
            toast(err?.response?.data?.detail || "Save failed.");
        } finally {
            setBusy(false);
        }
    };

    const origin = window.location.origin;
    const thumb = imageUrl
        ? imageUrl.startsWith("/")
            ? `${origin}${imageUrl}`
            : imageUrl
        : null;

    return (
        <div
            data-testid={`city-card-slot-${card.slot_index}`}
            className="bg-background"
        >
            <button
                type="button"
                onClick={onToggle}
                className="w-full text-left p-5 flex items-center gap-4 hover:bg-white/[0.04] transition-colors"
            >
                <div className="w-16 h-16 shrink-0 border border-line bg-white/[0.03] overflow-hidden grid place-items-center rounded-xl">
                    {thumb ? (
                        <img
                            src={thumb}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <span className="text-ink-soft text-[0.65rem] font-display tracking-[0.2em]">
                            {String(card.slot_index + 1).padStart(2, "0")}
                        </span>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-ink-soft">
                        Slot {String(card.slot_index + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display font-bold truncate">
                        {cityName || "-"}
                    </div>
                </div>
                <div className="text-xs text-ink-soft">
                    {open ? "Close" : "Edit"}
                </div>
            </button>
            {open && (
                <div className="p-5 border-t border-line space-y-4">
                    <div>
                        <label
                            className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1"
                            htmlFor={`city-${card.id}`}
                        >
                            City name
                        </label>
                        <input
                            id={`city-${card.id}`}
                            data-testid={`city-card-name-${card.slot_index}`}
                            value={cityName}
                            onChange={(e) => setCityName(e.target.value)}
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                        />
                    </div>
                    <div>
                        <label className="block text-[0.65rem] tracking-[0.25em] uppercase text-ink-soft mb-1">
                            Image URL
                        </label>
                        <input
                            data-testid={`city-card-image-url-${card.slot_index}`}
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                            placeholder="https:// or /api/files/…"
                            className="w-full bg-transparent border border-line rounded-lg px-3 py-2 outline-none focus:border-white/40"
                        />
                        <input
                            ref={fileRef}
                            type="file"
                            accept="image/*"
                            onChange={onUpload}
                            className="hidden"
                            data-testid={`city-card-upload-${card.slot_index}`}
                        />
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            className="vc-btn vc-btn-ghost mt-2 text-xs"
                            disabled={busy}
                            data-testid={`city-card-upload-btn-${card.slot_index}`}
                        >
                            {busy ? "Uploading…" : "Upload image"}
                        </button>
                    </div>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={busy}
                        className="vc-btn"
                        data-testid={`city-card-save-${card.slot_index}`}
                    >
                        {busy ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
}
